"use client";

import * as React from "react";
import { ArrowRight, ChevronDown, Headphones, FileText, Menu, ShieldCheck, Truck, UserCheck, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useWooAccountUrl } from "@/integrations/wordpress/useWooAccountUrl";
import { WordPressUserAuth } from "@/integrations/wordpress/WordPressUserAuth";
import styles from "./HomeCopy.module.css";
const HERO_IMAGE = "https://tektideslabs.com/wp-content/uploads/2026/09/Landing-page.png";
const LOGO_IMAGE = "https://tektideslabs.com/wp-content/uploads/2026/09/Logo-Real.png";
const HERO_SRCSET = ["https://tektideslabs.com/wp-content/uploads/2026/09/Landing-page-600x338.png 600w", "https://tektideslabs.com/wp-content/uploads/2026/09/Landing-page-768x432.png 768w", "https://tektideslabs.com/wp-content/uploads/2026/09/Landing-page-1024x576.png 1024w", "https://tektideslabs.com/wp-content/uploads/2026/09/Landing-page-1536x864.png 1536w", "https://tektideslabs.com/wp-content/uploads/2026/09/Landing-page.png 1672w"].join(", ");
const LOGO_SRCSET = ["https://tektideslabs.com/wp-content/uploads/2026/09/Logo-Real-300x100.png 300w", "https://tektideslabs.com/wp-content/uploads/2026/09/Logo-Real-600x200.png 600w"].join(", ");
const NAV_LINKS = [{
  label: "About",
  to: "/AboutPage"
}, {
  label: "FAQ",
  to: "/FaqPage"
}, {
  label: "Contact",
  to: "/ContactPage"
}, {
  label: "Policies",
  to: "/PoliciesPage"
}];
export default function HomeCopyPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isLoggedIn, setIsLoggedIn] = React.useState(false);
  const accountUrl = useWooAccountUrl();
  React.useEffect(() => {
    let mounted = true;
    async function checkUser() {
      try {
        const auth = new WordPressUserAuth();
        const res = await auth.getCurrentUser();
        if (mounted) {
          setIsLoggedIn(Boolean(res?.success && res?.data));
        }
      } catch {
        if (mounted) {
          setIsLoggedIn(false);
        }
      }
    }
    void checkUser();
    return () => {
      mounted = false;
    };
  }, []);
  const {
    ref: standardRef,
    isVisible: standardVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  const {
    ref: accountStepsRef,
    isVisible: accountStepsVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  const {
    ref: faqRef,
    isVisible: faqVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  const {
    ref: closingRef,
    isVisible: closingVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.15,
    once: true
  });
  const closeMobileMenu = () => setMobileMenuOpen(false);
  return <div className="min-h-screen overflow-x-hidden bg-[#020509] text-white">
      {/* =========================================================
          0. NOTICE BAR ABOVE THE HEADER
         ========================================================= */}
      <div className="relative z-50 border-b border-white/[0.08] bg-[#010307] px-4 py-2 text-center">
        <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/70 sm:text-[11px]">
          For laboratory research use only. Not for human or veterinary use. Adults 21 and over.
        </p>
      </div>

      {/* =========================================================
          1. PUBLIC LANDING HEADER
         ========================================================= */}
      <header className="sticky top-0 z-40 border-b border-white/[0.08] bg-[#020509]/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="group flex shrink-0 items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="TEK TIDES LABS home">
            <img src={LOGO_IMAGE} srcSet={LOGO_SRCSET} sizes="(min-width:1024px) 180px, 156px" alt="TEK TIDES LABS" width={2048} height={682} decoding="async" className="h-[52px] w-auto max-w-[180px] object-contain sm:h-[56px] sm:max-w-[210px] lg:h-[60px] lg:max-w-[230px]" />
          </Link>

          <nav className="hidden items-center gap-7 lg:gap-8 md:flex" aria-label="Landing navigation">
            {NAV_LINKS.map(item => <Link key={item.to} to={item.to} className="text-sm font-medium text-white/70 transition-colors hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[#020509] rounded-sm">
                {item.label}
              </Link>)}

            <Button asChild className="group h-11 rounded-lg bg-primary px-5 font-semibold text-primary-foreground shadow-[0_12px_35px_-15px_rgba(0,163,255,0.75)] hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary">
              {isLoggedIn ? <Link to="/ShopPage" className="inline-flex items-center justify-center gap-2">
                  View Research Catalog
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link> : <Link href={accountUrl} className="inline-flex items-center justify-center gap-2">
                  Create Your Account
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                </Link>}
            </Button>
          </nav>

          <button type="button" onClick={() => setMobileMenuOpen(current => !current)} className="flex size-11 items-center justify-center rounded-lg border border-white/10 bg-white/[0.03] text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden" aria-expanded={mobileMenuOpen} aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}>
            {mobileMenuOpen ? <X className="size-5" aria-hidden="true" /> : <Menu className="size-5" aria-hidden="true" />}
          </button>
        </div>

        {mobileMenuOpen && <nav className={`border-t border-white/[0.07] bg-[#020509]/95 px-4 py-5 backdrop-blur-xl md:hidden ${styles.mobileNavOpen}`} aria-label="Mobile landing navigation">
            <div className="mx-auto flex max-w-7xl flex-col gap-1.5">
              {NAV_LINKS.map(item => <Link key={item.to} to={item.to} onClick={closeMobileMenu} className="flex min-h-[44px] items-center rounded-lg px-3.5 py-2.5 text-base text-white/75 hover:bg-white/[0.04] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">
                  {item.label}
                </Link>)}

              <Button asChild className="mt-3 min-h-[48px] h-12 w-full bg-primary font-semibold text-primary-foreground focus-visible:ring-2 focus-visible:ring-primary">
                {isLoggedIn ? <Link to="/ShopPage" onClick={closeMobileMenu} className="inline-flex items-center justify-center gap-2">
                    View Research Catalog
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link> : <Link href={accountUrl} onClick={closeMobileMenu} className="inline-flex items-center justify-center gap-2">
                    Create Your Account
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>}
              </Button>
            </div>
          </nav>}
      </header>

      <main>
        {/* =========================================================
            2. HERO SECTION (h1)
           ========================================================= */}
        <section id="landing-hero" className="relative isolate overflow-hidden bg-[#020509] pt-6 sm:pt-10 lg:pt-12 pb-16 sm:pb-20" aria-labelledby="landing-heading">
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
            <div className={`absolute inset-0 ${styles.heroImageWrapper}`}>
              <img src={HERO_IMAGE} srcSet={HERO_SRCSET} sizes="100vw" alt="" width={1672} height={941} fetchPriority="high" decoding="async" loading="eager" className="absolute inset-0 size-full object-cover object-[68%_center] sm:object-[66%_center] lg:object-center" />
            </div>

            {/* CSS-only entrance dark cover layer */}
            <div className={`absolute inset-0 bg-[#020509] ${styles.heroCover}`} aria-hidden="true" />

            <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(2,5,9,0.97)_0%,rgba(2,5,9,0.88)_27%,rgba(2,5,9,0.48)_52%,rgba(2,5,9,0.08)_78%,rgba(2,5,9,0.10)_100%)]" />
            <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(2,5,9,0.48)_0%,rgba(2,5,9,0.58)_42%,rgba(2,5,9,0.94)_100%)] lg:bg-none" />
            <div className="absolute -left-[18%] top-[10%] size-[650px] rounded-full [background:radial-gradient(closest-side,color-mix(in_srgb,var(--primary)_5%,transparent)_0%,transparent_100%)]" />
            <div className="absolute inset-0 opacity-[0.09] [background-image:linear-gradient(rgba(255,255,255,0.055)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.055)_1px,transparent_1px)] [background-size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_92%)]" />

            <div className={`absolute inset-x-0 top-[20%] h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent ${styles.heroScanningLine}`} />

            <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#020509] via-[#020509]/65 to-transparent" />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="max-w-[760px]">
              {/* Brand identifier */}
              <div className={`mb-4 flex items-center gap-3 ${styles.heroItem}`}>
                <span className="h-px w-8 bg-primary" aria-hidden="true" />
                <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.28em] text-primary sm:text-[11px]">
                  TEK TIDES LABS
                </span>
              </div>

              {/* Primary hero headline (single h1 on page) */}
              <h1 id="landing-heading" className={`text-[clamp(2.5rem,10vw,4.5rem)] font-bold leading-[0.92] tracking-[-0.05em] text-white sm:text-[5.2rem] md:text-[6.2rem] lg:text-[7rem] ${styles.heroItem}`}>
                RESEARCH
                <span className="block bg-gradient-to-r from-[#00a3ff] via-[#24bdff] to-[#70d8ff] bg-clip-text text-transparent">
                  ELEVATED.
                </span>
              </h1>

              {/* Slogan */}
              <div className={`mt-4 flex items-center gap-3 sm:mt-5 sm:gap-4 ${styles.heroItem}`}>
                <span className="h-px w-8 shrink-0 bg-primary shadow-[0_0_10px_rgba(0,163,255,0.65)] sm:w-12" aria-hidden="true" />
                <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-primary sm:text-[13px] sm:tracking-[0.3em]">
                  We Rise With The Tide
                </p>
              </div>

              {/* Intro paragraph verbatim */}
              <p className={`mt-5 max-w-xl text-[15px] leading-relaxed text-white/75 sm:text-base sm:leading-7 ${styles.heroItem}`}>
                A focused research catalog. Clear information. A higher standard of customer experience.
              </p>

              {/* Buttons */}
              <div className={`mt-7 flex flex-col gap-3 sm:flex-row sm:items-start ${styles.heroItem}`}>
                <div className="flex flex-col gap-1.5 sm:max-w-xs">
                  <Button asChild size="lg" className="group relative min-h-[48px] h-12 w-full overflow-hidden rounded-lg border border-primary/70 bg-primary px-7 text-sm font-semibold uppercase tracking-[0.08em] text-primary-foreground shadow-[0_0_0_1px_rgba(0,163,255,0.15),0_18px_55px_-15px_rgba(0,163,255,0.9)] transition-all hover:-translate-y-0.5 hover:bg-[#19adff] focus-visible:ring-2 focus-visible:ring-primary">
                    {isLoggedIn ? <Link to="/ShopPage" className="inline-flex items-center justify-center gap-2.5">
                        View Research Catalog
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </Link> : <Link href={accountUrl} className="inline-flex items-center justify-center gap-2.5">
                        CREATE YOUR ACCOUNT
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                      </Link>}
                  </Button>
                  {!isLoggedIn && <Link href={accountUrl} className="inline-flex min-h-[44px] items-center gap-1.5 text-[12px] text-white/60 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                      <span>Already have an account? Log in</span>
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </Link>}
                  <span className="text-[11px] font-mono tracking-tight text-white/55">
                    Account sign-in and age verification required. For researchers aged 21 and over.
                  </span>
                </div>

                <Button asChild variant="outline" size="lg" className="min-h-[48px] h-12 w-full sm:w-auto rounded-lg border-white/20 bg-black/20 px-7 text-sm font-medium text-white backdrop-blur-md hover:border-primary/45 hover:bg-primary/[0.09] hover:text-white focus-visible:ring-2 focus-visible:ring-primary">
                  <Link to="/AboutPage">About TEK TIDES LABS</Link>
                </Button>
              </div>

              {/* Badges */}
              <div className={`mt-7 grid max-w-xl gap-3 border-t border-white/[0.10] pt-4 sm:grid-cols-3 ${styles.heroItem}`}>
                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-white/65">
                  <ShieldCheck className="size-4 shrink-0 text-primary" aria-hidden="true" />
                  Research Use Only
                </div>

                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-white/65">
                  <UserCheck className="size-4 shrink-0 text-primary" aria-hidden="true" />
                  Adults 21+
                </div>

                <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.12em] text-white/65">
                  <Truck className="size-4 shrink-0 text-primary" aria-hidden="true" />
                  U.S. Shipping
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            3. NEW SECTION 1: THE TEK TIDES STANDARD (id="standard")
           ========================================================= */}
        <section id="standard" ref={standardRef} aria-labelledby="standard-heading" className="scroll-mt-[88px] relative overflow-hidden border-t border-white/[0.07] bg-[#050b14] py-16 sm:py-24">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:64px_64px]" />
          </div>

          <div className={`relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${standardVisible ? styles.sectionVisible : styles.sectionHidden}`}>
            {/* Top row: Premium two-column layout on lg */}
            <div className="grid gap-10 lg:grid-cols-12 lg:items-center lg:gap-14">
              <div className="space-y-4 lg:col-span-7">
                <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-primary sm:text-[11px]">
                  THE TEK TIDES STANDARD
                </p>
                <h2 id="standard-heading" className="text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl lg:leading-tight">
                  Know the research. Know the source.
                </h2>
                <div className="h-px w-14 bg-primary/70" />
                <p className="pt-2 text-base leading-relaxed text-white/80 sm:text-lg sm:leading-8">
                  TEK TIDES LABS brings together clear product information, straightforward communication, and a research-focused customer experience.
                </p>
              </div>

              {/* Restrained decorative panel */}
              <div className="relative overflow-hidden rounded-xl border border-white/[0.08] bg-[#020509]/80 p-6 sm:p-8 backdrop-blur-md lg:col-span-5">
                <div className="pointer-events-none absolute -right-12 -top-12 size-48 rounded-full [background:radial-gradient(closest-side,color-mix(in_srgb,var(--primary)_22%,transparent)_0%,transparent_100%)]" aria-hidden="true" />
                <div className="relative z-10 flex flex-col items-start gap-4">
                  <div className="flex size-11 items-center justify-center rounded-lg border border-primary/30 bg-primary/[0.12] text-primary shadow-[0_0_20px_rgba(0,163,255,0.2)]">
                    <ShieldCheck className="size-6 text-primary" aria-hidden="true" />
                  </div>
                  <div>
                    <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-white">
                      Research Use Only
                    </span>
                    <p className="mt-1 font-mono text-[11px] leading-relaxed text-white/60">
                      Research-focused products supported by clear information and straightforward customer service.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Three elegant cards directly below */}
            <div className="mt-12 grid gap-6 md:grid-cols-3">
              {/* Card 1 */}
              <div className="group relative rounded-xl border border-white/[0.08] bg-[#020509]/75 p-6 backdrop-blur-md transition-all duration-200 hover:border-primary/40 hover:bg-[#071322]">
                <div className="flex size-9 items-center justify-center rounded-lg border border-primary/25 bg-primary/[0.08] text-primary">
                  <ShieldCheck className="size-4" aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">
                  Clear Product Information
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Straightforward details to help customers understand our research catalog.
                </p>
              </div>

              {/* Card 2 */}
              <div className="group relative rounded-xl border border-white/[0.08] bg-[#020509]/75 p-6 backdrop-blur-md transition-all duration-200 hover:border-primary/40 hover:bg-[#071322]">
                <div className="flex size-9 items-center justify-center rounded-lg border border-primary/25 bg-primary/[0.08] text-primary">
                  <FileText className="size-4" aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">
                  Documentation Inquiries
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Contact our team with questions about available product documentation.
                </p>
              </div>

              {/* Card 3 */}
              <div className="group relative rounded-xl border border-white/[0.08] bg-[#020509]/75 p-6 backdrop-blur-md transition-all duration-200 hover:border-primary/40 hover:bg-[#071322]">
                <div className="flex size-9 items-center justify-center rounded-lg border border-primary/25 bg-primary/[0.08] text-primary">
                  <Headphones className="size-4" aria-hidden="true" />
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">
                  Direct Customer Support
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Helpful assistance with product information, accounts, and orders.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            4. NEW SECTION: YOUR RESEARCH ACCOUNT (id="account-steps")
           ========================================================= */}
        <section id="account-steps" ref={accountStepsRef} aria-labelledby="account-steps-heading" className="scroll-mt-[88px] relative overflow-hidden border-t border-white/[0.07] bg-[#020509] pt-16 pb-14 sm:pt-24 sm:pb-20">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:64px_64px]" />
            <div className="absolute right-0 top-1/2 -translate-y-1/2 size-96 rounded-full [background:radial-gradient(closest-side,color-mix(in_srgb,var(--primary)_12%,transparent)_0%,transparent_100%)]" />
          </div>

          <div className={`relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${accountStepsVisible ? styles.sectionVisible : styles.sectionHidden}`}>
            <div className="max-w-2xl">
              <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-primary sm:text-[11px]">
                YOUR RESEARCH ACCOUNT
              </p>
              <h2 id="account-steps-heading" className="mt-2 text-2xl font-bold tracking-tight text-white uppercase sm:text-3xl lg:text-4xl">
                A FEW STEPS. A CLEAR START.
              </h2>
              <div className="mt-3 h-px w-14 bg-primary/70" />
            </div>

            {/* Three cards in grid gap-6 md:grid-cols-3 */}
            <div className="relative mt-12 grid gap-6 md:grid-cols-3">
              {/* Decorative connecting line between cards on md+ */}
              <div className="pointer-events-none absolute top-10 left-[16%] right-[16%] hidden h-px bg-gradient-to-r from-primary/10 via-primary/30 to-primary/10 md:block" aria-hidden="true" />

              {/* Step 01 */}
              <div className="group relative rounded-xl border border-white/[0.08] bg-[#071322]/80 p-6 sm:p-7 backdrop-blur-md transition-all duration-200 hover:border-primary/40 hover:bg-[#071322]">
                <div className="flex items-baseline justify-between border-b border-white/[0.07] pb-4">
                  <span className="font-mono text-2xl sm:text-3xl font-bold text-primary tracking-tight">
                    01
                  </span>
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
                    CREATE
                  </span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">
                  Create an account or sign in.
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Registration or login is required to access the catalog.
                </p>
              </div>

              {/* Step 02 */}
              <div className="group relative rounded-xl border border-white/[0.08] bg-[#071322]/80 p-6 sm:p-7 backdrop-blur-md transition-all duration-200 hover:border-primary/40 hover:bg-[#071322]">
                <div className="flex items-baseline justify-between border-b border-white/[0.07] pb-4">
                  <span className="font-mono text-2xl sm:text-3xl font-bold text-primary tracking-tight">
                    02
                  </span>
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
                    CONFIRM
                  </span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">
                  Confirm and acknowledge.
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Confirm you are 21 or older and acknowledge our research-use policies.
                </p>
              </div>

              {/* Step 03 */}
              <div className="group relative rounded-xl border border-white/[0.08] bg-[#071322]/80 p-6 sm:p-7 backdrop-blur-md transition-all duration-200 hover:border-primary/40 hover:bg-[#071322]">
                <div className="flex items-baseline justify-between border-b border-white/[0.07] pb-4">
                  <span className="font-mono text-2xl sm:text-3xl font-bold text-primary tracking-tight">
                    03
                  </span>
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-white/70">
                    EXPLORE
                  </span>
                </div>
                <h3 className="mt-4 text-base font-semibold text-white">
                  Explore the research catalog.
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-white/65">
                  Once signed in and verified, browse available research products.
                </p>
              </div>
            </div>

            {/* Below the cards: CTA button */}
            <div className="mt-10 flex flex-col items-start gap-2">
              <Button asChild size="lg" className="group min-h-[48px] h-12 w-full sm:w-auto rounded-lg bg-primary px-8 font-semibold uppercase tracking-[0.08em] text-primary-foreground shadow-[0_12px_35px_-15px_rgba(0,163,255,0.75)] hover:bg-primary/90 focus-visible:ring-2 focus-visible:ring-primary">
                {isLoggedIn ? <Link to="/ShopPage" className="inline-flex items-center justify-center gap-2.5">
                    View Research Catalog
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link> : <Link href={accountUrl} className="inline-flex items-center justify-center gap-2.5">
                    CREATE YOUR ACCOUNT
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </Link>}
              </Button>
              {!isLoggedIn && <Link href={accountUrl} className="inline-flex min-h-[44px] items-center gap-1.5 text-[12px] text-white/60 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                  <span>Already have an account? Log in</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>}
            </div>
          </div>
        </section>

        {/* =========================================================
            5. FAQ SECTION (id="faq")
           ========================================================= */}
        <section id="faq" ref={faqRef} aria-labelledby="faq-heading" className="scroll-mt-[88px] relative overflow-hidden border-t border-white/[0.07] bg-[#050b14] py-16 sm:py-24">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] [background-size:64px_64px]" />
          </div>

          <div className={`relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${faqVisible ? styles.sectionVisible : styles.sectionHidden}`}>
            <div className="grid gap-10 lg:grid-cols-12 lg:items-start lg:gap-14">
              {/* Left Column: Title block + single contact button */}
              <div className="space-y-4 lg:col-span-5">
                <div>
                  <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-primary">
                    A FEW THINGS TO KNOW
                  </p>
                  <h2 id="faq-heading" className="mt-2 text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
                    Good questions. A place to start.
                  </h2>
                  <div className="mt-3 h-px w-14 bg-primary/70" />
                </div>

                <div className="pt-2">
                  <Button asChild variant="outline" className="min-h-[48px] h-12 w-full sm:w-auto rounded-lg border-white/20 bg-white/[0.04] px-6 text-sm font-semibold text-white transition-colors hover:border-primary/50 hover:bg-primary/[0.12] hover:text-white focus-visible:ring-2 focus-visible:ring-primary">
                    <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                      <Headphones className="size-4 shrink-0 text-primary" aria-hidden="true" />
                      Contact TEK TIDES LABS
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Right Column: 3 <details> items */}
              <div className="divide-y divide-white/[0.08] lg:col-span-7">
                {/* FAQ Item 1 */}
                <details className="group py-5 first:pt-0 last:pb-0">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left font-medium text-white transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                    <span className="text-base sm:text-lg">Why do I need an account?</span>
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-white/60 transition-transform duration-200 group-open:rotate-180 group-open:text-primary">
                      <ChevronDown className="size-4" aria-hidden="true" />
                    </span>
                  </summary>
                  <div className="mt-3 text-sm leading-relaxed text-white/75 pr-8">
                    The research catalog is available to signed-in customers who are 21 or older and have acknowledged our research-use policies.
                  </div>
                </details>

                {/* FAQ Item 2 */}
                <details className="group py-5 first:pt-0 last:pb-0">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left font-medium text-white transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                    <span className="text-base sm:text-lg">Who is TEK TIDES LABS for?</span>
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-white/60 transition-transform duration-200 group-open:rotate-180 group-open:text-primary">
                      <ChevronDown className="size-4" aria-hidden="true" />
                    </span>
                  </summary>
                  <div className="mt-3 text-sm leading-relaxed text-white/75 pr-8">
                    Adults aged 21 and over purchasing for legitimate laboratory research. Products are not for human or veterinary use.
                  </div>
                </details>

                {/* FAQ Item 3 */}
                <details className="group py-5 first:pt-0 last:pb-0">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-left font-medium text-white transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                    <span className="text-base sm:text-lg">Where can I ask about product information or documentation?</span>
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-white/10 bg-white/[0.03] text-white/60 transition-transform duration-200 group-open:rotate-180 group-open:text-primary">
                      <ChevronDown className="size-4" aria-hidden="true" />
                    </span>
                  </summary>
                  <div className="mt-3 text-sm leading-relaxed text-white/75 pr-8">
                    <Link to="/ContactPage" className="text-primary underline underline-offset-2 hover:text-[#56c6ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-xs">
                      Contact Customer Support
                    </Link>
                    {" "}and our team will help.
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        {/* =========================================================
            7. NEW CLOSING SECTION (id="closing")
           ========================================================= */}
        <section id="closing" ref={closingRef} aria-labelledby="closing-heading" className="scroll-mt-[88px] relative overflow-hidden border-t border-white/[0.07] bg-gradient-to-b from-[#020509] via-[#050b14] to-[#061526] py-20 sm:py-28">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            {/* Faint technical grid */}
            <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(255,255,255,0.06)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.06)_1px,transparent_1px)] [background-size:64px_64px]" />
            {/* Soft radial primary glow centered */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 size-[520px] rounded-full [background:radial-gradient(closest-side,color-mix(in_srgb,var(--primary)_16%,transparent)_0%,transparent_100%)]" />

            {/* Decorative thin wave lines along the bottom */}
            <div className={`absolute -bottom-1 inset-x-0 h-28 overflow-hidden opacity-30 ${styles.closingWaveDrift}`}>
              <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-[110%] min-w-[1000px] h-full" preserveAspectRatio="none">
                <path d="M0 60 C 240 15, 480 105, 720 50 C 960 -5, 1200 95, 1440 40" stroke="var(--primary)" strokeWidth="1.25" strokeOpacity="0.45" />
                <path d="M0 80 C 280 35, 520 115, 780 70 C 1040 25, 1260 105, 1440 65" stroke="var(--primary)" strokeWidth="1" strokeOpacity="0.3" />
                <path d="M0 100 C 320 65, 580 120, 840 90 C 1100 60, 1320 110, 1440 85" stroke="var(--primary)" strokeWidth="0.75" strokeOpacity="0.2" />
              </svg>
            </div>
          </div>

          <div className={`relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center ${closingVisible ? styles.sectionVisible : styles.sectionHidden}`}>
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.28em] text-primary sm:text-xs">
              THE TIDE IS RISING
            </p>
            <h2 id="closing-heading" className="mt-4 font-bold tracking-tight text-white uppercase leading-[1.08]">
              <span className="block text-[clamp(1.5rem,4.5vw,2.5rem)] text-white">
                RESEARCH MOVES FORWARD.
              </span>
              <span className="mt-2 block text-[clamp(1.9rem,6.2vw,3.5rem)] font-extrabold whitespace-nowrap bg-gradient-to-r from-[#00a3ff] via-[#24bdff] to-[#70d8ff] bg-clip-text text-transparent">
                SO DO WE.
              </span>
            </h2>
            <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg">
              Discover the TEK TIDES LABS research experience.
            </p>
          </div>
        </section>
      </main>

      {/* =========================================================
          8. PUBLIC LANDING FOOTER
         ========================================================= */}
      <footer className="relative border-t border-white/[0.08] bg-gradient-to-b from-[#030b16] via-[#020710] to-[#010408]">
        {/* Thin restrained electric-blue accent line fading at the edges */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" aria-hidden="true" />

        <div className="mx-auto max-w-7xl px-4 pt-14 pb-10 sm:px-6 sm:pt-16 sm:pb-12 lg:px-8">
          {/* Main row: Two columns top-aligned on desktop */}
          <div className="grid gap-10 md:grid-cols-12 md:items-start md:gap-8">
            {/* Left: Brand column */}
            <div className="space-y-3.5 md:col-span-7 lg:col-span-8 max-w-md">
              <div className="flex items-center">
                <Link to="/" aria-label="TEK TIDES LABS home" className="inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm">
                  <img src={LOGO_IMAGE} srcSet={LOGO_SRCSET} sizes="(min-width:1024px) 180px, 156px" alt="TEK TIDES LABS" width={2048} height={682} loading="lazy" decoding="async" className="h-[52px] w-auto max-w-[200px] object-contain sm:h-[58px] sm:max-w-[230px]" />
                </Link>
              </div>

              <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                WE RISE WITH THE TIDE.
              </p>

              <p className="text-sm leading-relaxed text-white/80">
                Made for research. Built with the tide.
              </p>
            </div>

            {/* Right: Navigation block */}
            <div className="md:col-span-5 lg:col-span-4 md:flex md:justify-end">
              <nav className="grid grid-cols-2 gap-x-8 gap-y-3 sm:gap-x-10 text-sm md:text-right" aria-label="Footer navigation">
                {NAV_LINKS.map(item => <Link key={item.to} to={item.to} className="text-white/75 transition-colors hover:text-[#38bdf8] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm py-1">
                    {item.label}
                  </Link>)}

                <Link to="/ShopPage" className="col-span-2 mt-1 inline-flex items-center gap-1.5 font-medium text-primary transition-colors hover:text-[#70d8ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-sm py-1 md:justify-end">
                  <span>View Research Catalog</span>
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </nav>
            </div>
          </div>

          {/* Verbatim RUO disclaimer */}
          <div className="mt-10 border-t border-white/[0.08] pt-6">
            <p className="max-w-3xl text-xs sm:text-sm leading-relaxed text-white/70">
              Products are supplied strictly for laboratory research use only and are not intended for diagnostic, clinical, therapeutic, human, or veterinary applications.
            </p>
          </div>

          {/* Bottom bar */}
          <div className="mt-6 flex flex-col gap-3 border-t border-white/[0.06] pt-6 font-mono text-[10px] uppercase tracking-[0.16em] text-white/65 sm:flex-row sm:items-center sm:justify-between">
            <span>© {new Date().getFullYear()} TEK TIDES LABS LLC</span>
            <span>Research Use Only | Adults 21+</span>
          </div>
        </div>
      </footer>
    </div>;
}